import { z } from "zod";

import type { SharePackage } from "../share-types";
import { offerSchema } from "../reviews/schemas";
import { receivedSchema } from "../../studio/register/schemas";
import type { CreatedShareLink, ShareLink } from "./types";

/** The service sends `null` where TypeScript has `undefined`. */
const maybe = <T extends z.ZodTypeAny>(schema: T) =>
  schema.nullish().transform((value): z.output<T> | undefined => value ?? undefined);

const linkSchema = z.object({
  id: z.string(),
  projectId: z.string(),
  drawing: z.string(),
  drawingName: z.string(),
  rights: z.enum(["view", "comment"]),
  label: maybe(z.string()),
  hasPassword: z.boolean(),
  createdBy: z.string(),
  createdAt: z.string(),
  expiresAt: maybe(z.string()),
  status: z.enum(["active", "expired", "revoked"]),
  revokedAt: maybe(z.string()),
  lastUsedAt: maybe(z.string()),
  opens: z.number(),
});

export const parseLink = (value: unknown): ShareLink => linkSchema.parse(value);
export const parseLinks = (value: unknown): ShareLink[] => z.array(linkSchema).parse(value);
export const parseCreated = (value: unknown): CreatedShareLink =>
  z.object({ link: linkSchema, url: z.string() }).parse(value);

/** What identity's `/links/open` answers (`crates/services/identity/src/links/views.rs`, `Opened`). */
const packageSchema = z.object({
  linkId: z.string(),
  title: z.string(),
  projectName: z.string(),
  firmName: z.string(),
  senderName: z.string(),
  rights: z.enum(["view", "comment"]),
  expiresAt: maybe(z.string()),
  organisationId: z.string(),
  drawingId: z.string(),
  drawingKind: z.enum(["plan", "site", "section", "elevation", "detail", "layout"]),
  /** Present when the link is a client review. */
  review: maybe(offerSchema),
  /** Present when the link delivers a transmittal. */
  transmittal: maybe(receivedSchema),
});

export const openedSchema = z.discriminatedUnion("status", [
  z.object({ status: z.literal("ok"), package: packageSchema }),
  z.object({
    status: z.literal("password"),
    firmName: z.string(),
    attemptsLeft: z.number(),
    wrong: z.boolean(),
  }),
  z.object({ status: z.literal("locked"), firmName: z.string(), until: z.string() }),
  z.object({ status: z.literal("expired"), firmName: z.string(), senderName: z.string() }),
  z.object({ status: z.literal("revoked"), firmName: z.string(), senderName: z.string() }),
  z.object({ status: z.literal("disabled"), firmName: z.string() }),
  z.object({ status: z.literal("notFound") }),
]);

export type LivePackage = z.infer<typeof packageSchema>;

/** A live link as the share viewer shows it: one drawing, view only, from the doc service. */
export function packageOf(live: LivePackage): SharePackage {
  return {
    linkId: live.linkId,
    title: live.title,
    message: undefined,
    projectName: live.projectName,
    firmName: live.firmName,
    senderName: live.senderName,
    items: [
      {
        id: live.drawingId,
        name: live.title,
        format: "drawing",
        kind: live.drawingKind,
        number: undefined,
        scale: "",
        seed: 0,
      },
    ],
    rights: { view: true, comment: false, download: false },
    expiresAt: live.expiresAt,
    watermark: "",
    live: { organisationId: live.organisationId, drawingId: live.drawingId },
    review: live.review,
    transmittal: live.transmittal,
  };
}
